import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openTawkChat } from '../common/TawkTo';
import './UserSections.css';

const UserSectionSupport = () => {
  const { t } = useTranslation('user');
  const [activeTab, setActiveTab] = useState('help-center');
  const [searchQuery, setSearchQuery] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const tabs = [
    { id: 'help-center', label: t('user.support.tabs.helpCenter'), icon: 'help' },
    { id: 'live-chat', label: t('user.support.tabs.liveChat'), icon: 'chat' },
    { id: 'tickets', label: t('user.support.tabs.myTickets'), icon: 'confirmation_number' },
    { id: 'ai-assistant', label: t('user.support.tabs.aiAssistant'), icon: 'smart_toy' },
    { id: 'contact', label: t('user.support.tabs.contactUs'), icon: 'contact_support' },
    { id: 'trust-safety', label: t('user.support.tabs.trustSafety'), icon: 'security' }
  ];

  const helpCategories = [
    {
      id: 'learning',
      name: t('user.support.helpCenter.learning'),
      icon: 'school',
      color: '#3b82f6',
      articles: [
        {
          title: t('user.support.helpCenter.articles.vocabularyNotUnlocking.title'),
          content: t('user.support.helpCenter.articles.vocabularyNotUnlocking.content'),
          tags: ['vocabulary', 'unlock', 'progress']
        },
        {
          title: t('user.support.helpCenter.articles.trackProgress.title'),
          content: t('user.support.helpCenter.articles.trackProgress.content'),
          tags: ['progress', 'tracking', 'dashboard']
        },
        {
          title: t('user.support.helpCenter.articles.retakeLessons.title'),
          content: t('user.support.helpCenter.articles.retakeLessons.content'),
          tags: ['lessons', 'retake', 'progress']
        }
      ]
    },
    {
      id: 'billing',
      name: t('user.support.helpCenter.billingSubscription'),
      icon: 'payment',
      color: '#10b981',
      articles: [
        {
          title: t('user.support.helpCenter.articles.cancelSubscription.title'),
          content: t('user.support.helpCenter.articles.cancelSubscription.content'),
          tags: ['subscription', 'cancel', 'billing']
        },
        {
          title: t('user.support.helpCenter.articles.changePaymentMethod.title'),
          content: t('user.support.helpCenter.articles.changePaymentMethod.content'),
          tags: ['payment', 'billing', 'methods']
        },
        {
          title: t('user.support.helpCenter.articles.trialEnds.title'),
          content: t('user.support.helpCenter.articles.trialEnds.content'),
          tags: ['trial', 'billing', 'subscription']
        }
      ]
    },
    {
      id: 'technical',
      name: t('user.support.helpCenter.technicalIssues'),
      icon: 'build',
      color: '#f59e0b',
      articles: [
        {
          title: t('user.support.helpCenter.articles.cantJoinZoom.title'),
          content: t('user.support.helpCenter.articles.cantJoinZoom.content'),
          tags: ['zoom', 'live-sessions', 'technical']
        },
        {
          title: t('user.support.helpCenter.articles.videoNotLoading.title'),
          content: t('user.support.helpCenter.articles.videoNotLoading.content'),
          tags: ['video', 'loading', 'technical']
        },
        {
          title: t('user.support.helpCenter.articles.appCrashing.title'),
          content: t('user.support.helpCenter.articles.appCrashing.content'),
          tags: ['app', 'crash', 'technical']
        }
      ]
    },
    {
      id: 'security',
      name: t('user.support.helpCenter.securityPrivacy'),
      icon: 'security',
      color: '#ef4444',
      articles: [
        {
          title: t('user.support.helpCenter.articles.enable2FA.title'),
          content: t('user.support.helpCenter.articles.enable2FA.content'),
          tags: ['security', '2fa', 'authentication']
        },
        {
          title: t('user.support.helpCenter.articles.forgotPassword.title'),
          content: t('user.support.helpCenter.articles.forgotPassword.content'),
          tags: ['password', 'reset', 'security']
        },
        {
          title: t('user.support.helpCenter.articles.dataSecure.title'),
          content: t('user.support.helpCenter.articles.dataSecure.content'),
          tags: ['privacy', 'security', 'data']
        }
      ]
    }
  ];

  const supportTickets = [
    {
      id: 'TKT-2024-001',
      subject: 'Cannot access premium content',
      category: 'Technical',
      status: 'In Progress',
      priority: 'High',
      createdAt: '2024-12-10',
      lastUpdated: '2024-12-10',
      description: 'I upgraded to premium but still can\'t access premium articles.'
    },
    {
      id: 'TKT-2024-002',
      subject: 'Billing question',
      category: 'Billing',
      status: 'Resolved',
      priority: 'Medium',
      createdAt: '2024-12-08',
      lastUpdated: '2024-12-09',
      description: 'I was charged twice this month. Need clarification.'
    },
    {
      id: 'TKT-2024-003',
      subject: 'Feature request',
      category: 'Feedback',
      status: 'Open',
      priority: 'Low',
      createdAt: '2024-12-05',
      lastUpdated: '2024-12-05',
      description: 'Would love to have offline mode for mobile app.'
    }
  ];

  const chatHistory = [
    {
      id: 1,
      agent: 'Sarah M.',
      date: '2024-12-10',
      duration: '15 min',
      status: 'Resolved',
      topic: 'Password reset'
    },
    {
      id: 2,
      agent: 'Ahmed K.',
      date: '2024-12-08',
      duration: '8 min',
      status: 'Resolved',
      topic: 'Subscription upgrade'
    }
  ];

  const aiSuggestions = [
    t('user.support.aiAssistant.suggestions.resetPassword'),
    t('user.support.aiAssistant.suggestions.currentPlan'),
    t('user.support.aiAssistant.suggestions.unlockArticle'),
    t('user.support.aiAssistant.suggestions.joinSessions'),
    t('user.support.aiAssistant.suggestions.cancelSubscription'),
    t('user.support.aiAssistant.suggestions.videoPlayback')
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'help-center':
  return (
          <div className="support-tab-content">
            <div className="support-header">
              <h3>{t('user.support.helpCenter.title')}</h3>
              <p>{t('user.support.helpCenter.subtitle')}</p>
            </div>

            {/* Search Bar */}
            <div className="search-section">
              <div className="search-container">
                <span className="material-icons search-icon">search</span>
                <input
                  type="text"
                  placeholder={t('user.support.helpCenter.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                <button className="search-btn">
                  <span className="material-icons">search</span>
                </button>
              </div>
              {searchQuery && (
                <div className="search-suggestions">
                  <h4>{t('user.support.helpCenter.aiSuggestions')}</h4>
                  <div className="suggestion-tags">
                    {aiSuggestions.map((suggestion, index) => (
                      <button key={index} className="suggestion-tag">
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
          </div>
          
            {/* Category Filter */}
            <div className="category-filter">
              <button
                className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('all')}
              >
                {t('user.support.helpCenter.allCategories')}
              </button>
              {helpCategories.map(category => (
                <button
                  key={category.id}
                  className={`filter-btn ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <span className="material-icons" style={{ color: category.color }}>
                    {category.icon}
                  </span>
                  {category.name}
                </button>
              ))}
          </div>
          
            {/* Help Articles Grid */}
            <div className="help-articles-grid">
              {helpCategories
                .filter(category => selectedCategory === 'all' || category.id === selectedCategory)
                .map(category => (
                  <div key={category.id} className="category-section">
                    <div className="category-header">
                      <span className="material-icons" style={{ color: category.color }}>
                        {category.icon}
                      </span>
                      <h4>{category.name}</h4>
                    </div>
                    <div className="articles-list">
                      {category.articles.map((article, index) => (
                        <div key={index} className="article-card">
                          <div className="article-header">
                            <h5>{article.title}</h5>
                            <span className="material-icons">expand_more</span>
                          </div>
                          <div className="article-content">
                            <p>{article.content}</p>
                            <div className="article-tags">
                              {article.tags.map((tag, tagIndex) => (
                                <span key={tagIndex} className="article-tag">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
          </div>
          
            {/* Quick Actions */}
            <div className="quick-actions">
              <h4>{t('user.support.helpCenter.quickActions')}</h4>
              <div className="action-buttons">
                <button className="support-btn primary">
                  <span className="material-icons">article</span>
                  {t('user.support.helpCenter.browseAllArticles')}
                </button>
                <button className="support-btn secondary">
                  <span className="material-icons">video_library</span>
                  {t('user.support.helpCenter.videoTutorials')}
                </button>
                <button className="support-btn secondary">
                  <span className="material-icons">download</span>
                  {t('user.support.helpCenter.downloadGuides')}
                </button>
              </div>
            </div>
          </div>
        );

      case 'live-chat':
        return (
          <div className="support-tab-content">
            <div className="support-header">
              <h3>{t('user.support.liveChat.title')}</h3>
              <p>{t('user.support.liveChat.subtitle')}</p>
            </div>

            <div className="chat-section">
              {!isChatOpen ? (
                <div className="chat-welcome">
                  <div className="chat-status">
                    <div className="status-indicator online">
                      <span className="status-dot"></span>
                      <span>{t('user.support.liveChat.supportOnline')}</span>
                    </div>
                    <p>{t('user.support.liveChat.averageResponse')}</p>
                  </div>
                  
                  <div className="chat-info">
                    <div className="info-card">
                      <span className="material-icons">schedule</span>
                      <div>
                        <h5>{t('user.support.liveChat.availableHours')}</h5>
                        <p>{t('user.support.liveChat.hoursWeekday')}</p>
                        <p>{t('user.support.liveChat.hoursSaturday')}</p>
                      </div>
                    </div>
                    
                    <div className="info-card">
                      <span className="material-icons">language</span>
                      <div>
                        <h5>{t('user.support.liveChat.languages')}</h5>
                        <p>{t('user.support.liveChat.languagesList')}</p>
          </div>
        </div>
      </div>
      
                  <button 
                    className="support-btn primary large"
                    onClick={() => { openTawkChat(); setIsChatOpen(true); }}
                  >
                    <span className="material-icons">chat</span>
                    {t('user.support.liveChat.startLiveChat')}
                  </button>
                </div>
              ) : (
                <div className="chat-interface">
                  <div className="chat-header">
                    <div className="chat-agent">
                      <img src="/agent-avatar.png" alt="Support Agent" className="agent-avatar" />
                      <div>
                        <h5>Sarah M.</h5>
                        <span className="agent-status">{t('user.support.liveChat.agentOnline')}</span>
                      </div>
                    </div>
                    <button 
                      className="close-chat"
                      onClick={() => setIsChatOpen(false)}
                    >
                      <span className="material-icons">close</span>
                    </button>
          </div>
          
                  <div className="chat-messages">
                    <div className="message agent">
                      <div className="message-content">
                        <p>{t('user.support.liveChat.agentGreeting')}</p>
                        <span className="message-time">2:30 PM</span>
                      </div>
                    </div>
          </div>
          
                  <div className="chat-input">
                    <input 
                      type="text" 
                      placeholder={t('user.support.liveChat.typeMessage')}
                      className="message-input"
                    />
                    <button className="send-btn">
                      <span className="material-icons">send</span>
                    </button>
                  </div>
                </div>
              )}
          </div>
          
            {/* Chat History */}
            <div className="chat-history">
              <h4>{t('user.support.liveChat.recentConversations')}</h4>
              <div className="history-list">
                {chatHistory.map(chat => (
                  <div key={chat.id} className="history-item">
                    <div className="history-info">
                      <h5>{chat.topic}</h5>
                      <p>{t('user.support.liveChat.agent')} {chat.agent}</p>
                      <p>{t('user.support.liveChat.date')} {chat.date} • {t('user.support.liveChat.duration')} {chat.duration}</p>
                    </div>
                    <div className="history-status">
                      <span className={`status-badge ${chat.status.toLowerCase()}`}>
                        {t('user.support.liveChat.resolved')}
                      </span>
                      <button className="view-transcript">
                        <span className="material-icons">visibility</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'tickets':
        return (
          <div className="support-tab-content">
            <div className="support-header">
              <h3>{t('user.support.tickets.title')}</h3>
              <p>{t('user.support.tickets.subtitle')}</p>
            </div>

            <div className="tickets-section">
              <div className="tickets-header">
                <button className="support-btn primary">
                  <span className="material-icons">add</span>
                  {t('user.support.tickets.submitNewTicket')}
                </button>
                <div className="tickets-filter">
                  <select defaultValue="all">
                    <option value="all">{t('user.support.tickets.allStatus')}</option>
                    <option value="open">{t('user.support.tickets.open')}</option>
                    <option value="in-progress">{t('user.support.tickets.inProgress')}</option>
                    <option value="resolved">{t('user.support.tickets.resolved')}</option>
                  </select>
                </div>
              </div>

              <div className="tickets-list">
                {supportTickets.map(ticket => (
                  <div key={ticket.id} className="ticket-card">
                    <div className="ticket-header">
                      <div className="ticket-info">
                        <h5>{ticket.subject}</h5>
                        <p className="ticket-id">#{ticket.id}</p>
                      </div>
                      <div className="ticket-meta">
                        <span className={`status-badge ${ticket.status.toLowerCase().replace(' ', '-')}`}>
                          {ticket.status}
                        </span>
                        <span className={`priority-badge ${ticket.priority.toLowerCase()}`}>
                          {ticket.priority}
                        </span>
                      </div>
                    </div>
                    
                    <div className="ticket-details">
                      <p>{ticket.description}</p>
                      <div className="ticket-category">
                        <span className="material-icons">category</span>
                        {ticket.category}
                      </div>
          </div>
          
                    <div className="ticket-footer">
                      <div className="ticket-dates">
                        <span>{t('user.support.tickets.created')} {ticket.createdAt}</span>
                        <span>{t('user.support.tickets.updated')} {ticket.lastUpdated}</span>
                      </div>
                      <div className="ticket-actions">
                        <button className="support-btn secondary small">
                          <span className="material-icons">visibility</span>
                          {t('user.support.tickets.view')}
                        </button>
                        <button className="support-btn secondary small">
                          <span className="material-icons">reply</span>
                          {t('user.support.tickets.reply')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'ai-assistant':
        return (
          <div className="support-tab-content">
            <div className="support-header">
              <h3>{t('user.support.aiAssistant.title')}</h3>
              <p>{t('user.support.aiAssistant.subtitle')}</p>
            </div>

            <div className="ai-section">
              <div className="ai-chat">
                <div className="ai-messages">
                  <div className="message ai">
                    <div className="message-content">
                      <p>{t('user.support.aiAssistant.greeting')}</p>
                      <ul>
                        <li>{t('user.support.aiAssistant.canHelp.account')}</li>
                        <li>{t('user.support.aiAssistant.canHelp.technical')}</li>
                        <li>{t('user.support.aiAssistant.canHelp.learning')}</li>
                        <li>{t('user.support.aiAssistant.canHelp.general')}</li>
                      </ul>
                      <p>{t('user.support.aiAssistant.howCanHelp')}</p>
                      <span className="message-time">Just now</span>
                    </div>
                  </div>
                </div>
                
                <div className="ai-input">
                  <input 
                    type="text" 
                    placeholder={t('user.support.aiAssistant.askAnything')}
                    className="ai-message-input"
                  />
                  <button className="send-btn">
                    <span className="material-icons">send</span>
                  </button>
                </div>
              </div>

              <div className="ai-suggestions">
                <h4>{t('user.support.aiAssistant.quickQuestions')}</h4>
                <div className="suggestion-grid">
                  {aiSuggestions.map((suggestion, index) => (
                    <button key={index} className="ai-suggestion">
                      {suggestion}
                    </button>
                  ))}
        </div>
      </div>
      
              <div className="ai-capabilities">
                <h4>{t('user.support.aiAssistant.whatCanHelp')}</h4>
                <div className="capabilities-grid">
                  <div className="capability-card">
                    <span className="material-icons">account_circle</span>
                    <h5>{t('user.support.aiAssistant.accountIssues.title')}</h5>
                    <p>{t('user.support.aiAssistant.accountIssues.description')}</p>
                  </div>
                  <div className="capability-card">
                    <span className="material-icons">subscriptions</span>
                    <h5>{t('user.support.aiAssistant.billingPlans.title')}</h5>
                    <p>{t('user.support.aiAssistant.billingPlans.description')}</p>
                  </div>
                  <div className="capability-card">
                    <span className="material-icons">school</span>
                    <h5>{t('user.support.aiAssistant.learningSupport.title')}</h5>
                    <p>{t('user.support.aiAssistant.learningSupport.description')}</p>
                  </div>
                  <div className="capability-card">
                    <span className="material-icons">build</span>
                    <h5>{t('user.support.aiAssistant.technicalHelp.title')}</h5>
                    <p>{t('user.support.aiAssistant.technicalHelp.description')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'contact':
        return (
          <div className="support-tab-content">
            <div className="support-header">
              <h3>{t('user.support.contact.title')}</h3>
              <p>{t('user.support.contact.subtitle')}</p>
            </div>

            <div className="contact-section">
              <div className="contact-grid">
                <div className="contact-card primary">
                  <div className="contact-icon">
                    <span className="material-icons">email</span>
                  </div>
                  <h4>{t('user.support.contact.emailSupport.title')}</h4>
                  <p>{t('user.support.contact.emailSupport.description')}</p>
                  <div className="contact-details">
                    <p><strong>{t('user.support.contact.emailSupport.email')}</strong> support@nativelike.com</p>
                    <p><strong>{t('user.support.contact.emailSupport.responseTime')}</strong> {t('user.support.contact.emailSupport.within24Hours')}</p>
                    <p><strong>{t('user.support.contact.emailSupport.available')}</strong> {t('user.support.contact.emailSupport.available24_7')}</p>
                  </div>
                  <a href="mailto:support@nativelike.com" className="support-btn primary">
                    <span className="material-icons">email</span>
                    {t('user.support.contact.emailSupport.sendEmail')}
                  </a>
          </div>
          
          <div className="contact-card">
                  <div className="contact-icon">
                    <span className="material-icons">chat</span>
                  </div>
            <h4>{t('user.support.contact.liveChat.title')}</h4>
                  <p>{t('user.support.contact.liveChat.description')}</p>
                  <div className="contact-details">
                    <p><strong>{t('user.support.contact.liveChat.available')}</strong> Mon-Fri 9AM-6PM GMT</p>
                    <p><strong>{t('user.support.contact.liveChat.languages')}</strong> EN, AR, FR</p>
                    <p><strong>{t('user.support.contact.liveChat.response')}</strong> {t('user.support.contact.liveChat.responseTime')}</p>
                  </div>
                  <button className="support-btn secondary" onClick={openTawkChat}>
                    <span className="material-icons">chat</span>
                    {t('user.support.contact.liveChat.startChat')}
                  </button>
          </div>
          
          <div className="contact-card">
                  <div className="contact-icon">
                    <span className="material-icons">phone</span>
                  </div>
            <h4>{t('user.support.contact.phoneSupport.title')}</h4>
            <p>{t('user.support.contact.phoneSupport.description')}</p>
                  <div className="contact-details">
                    <p><strong>{t('user.support.contact.phoneSupport.phone')}</strong> +212 6 12 34 56 78</p>
                    <p><strong>{t('user.support.contact.phoneSupport.available')}</strong> Mon-Fri 9AM-6PM GMT</p>
                    <p><strong>{t('user.support.contact.phoneSupport.for')}</strong> {t('user.support.contact.phoneSupport.premiumOnly')}</p>
                  </div>
                  <a href="tel:+212612345678" className="support-btn secondary">
                    <span className="material-icons">phone</span>
                    {t('user.support.contact.phoneSupport.callNow')}
                  </a>
                </div>

                <div className="contact-card">
                  <div className="contact-icon">
                    <span className="material-icons">whatsapp</span>
                  </div>
                  <h4>{t('user.support.contact.whatsappSupport.title')}</h4>
                  <p>{t('user.support.contact.whatsappSupport.description')}</p>
                  <div className="contact-details">
                    <p><strong>{t('user.support.contact.whatsappSupport.number')}</strong> +212 6 12 34 56 78</p>
                    <p><strong>{t('user.support.contact.whatsappSupport.available')}</strong> Mon-Fri 9AM-6PM GMT</p>
                    <p><strong>{t('user.support.contact.whatsappSupport.response')}</strong> {t('user.support.contact.whatsappSupport.responseTime')}</p>
          </div>
                  <a href="https://wa.me/212612345678" className="support-btn secondary">
                    <span className="material-icons">whatsapp</span>
                    {t('user.support.contact.whatsappSupport.whatsapp')}
                  </a>
        </div>
      </div>
      
              <div className="contact-form">
                <h4>{t('user.support.contact.submitRequest.title')}</h4>
                <form className="request-form">
                  <div className="form-row">
          <div className="form-group">
                      <label>{t('user.support.contact.submitRequest.category')}</label>
                      <select required>
                        <option value="">{t('user.support.contact.submitRequest.selectCategory')}</option>
              <option value="bug">{t('user.support.contact.submitRequest.bugReport')}</option>
                        <option value="billing">{t('user.support.contact.submitRequest.billingIssue')}</option>
                        <option value="account">{t('user.support.contact.submitRequest.accountProblem')}</option>
                        <option value="technical">{t('user.support.contact.submitRequest.technicalIssue')}</option>
              <option value="feature">{t('user.support.contact.submitRequest.featureRequest')}</option>
                        <option value="feedback">{t('user.support.contact.submitRequest.generalFeedback')}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('user.support.contact.submitRequest.priority')}</label>
                      <select required>
                        <option value="">{t('user.support.contact.submitRequest.selectPriority')}</option>
                        <option value="low">{t('user.support.contact.submitRequest.low')}</option>
                        <option value="medium">{t('user.support.contact.submitRequest.medium')}</option>
                        <option value="high">{t('user.support.contact.submitRequest.high')}</option>
                        <option value="urgent">{t('user.support.contact.submitRequest.urgent')}</option>
            </select>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>{t('user.support.contact.submitRequest.subject')}</label>
                    <input type="text" placeholder={t('user.support.contact.submitRequest.subjectPlaceholder')} required />
                  </div>
                  
                  <div className="form-group">
                    <label>{t('user.support.contact.submitRequest.description')}</label>
                    <textarea 
                      rows="5" 
                      placeholder={t('user.support.contact.submitRequest.descriptionPlaceholder')}
                      required
                    ></textarea>
          </div>
          
          <div className="form-group">
                    <label>{t('user.support.contact.submitRequest.attachments')}</label>
                    <div className="file-upload">
                      <input type="file" multiple />
                      <div className="upload-hint">
                        <span className="material-icons">attach_file</span>
                        <span>{t('user.support.contact.submitRequest.dragFiles')}</span>
                        <small>{t('user.support.contact.submitRequest.maxSize')}</small>
                      </div>
                    </div>
                  </div>
                  
                  <button type="submit" className="support-btn primary">
                    <span className="material-icons">send</span>
                    {t('user.support.contact.submitRequest.submitRequest')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        );

      case 'trust-safety':
        return (
          <div className="support-tab-content">
            <div className="support-header">
              <h3>{t('user.support.trustSafety.title')}</h3>
              <p>{t('user.support.trustSafety.subtitle')}</p>
            </div>

            <div className="trust-safety-section">
              <div className="safety-grid">
                <div className="safety-card">
                  <div className="safety-icon">
                    <span className="material-icons">report</span>
                  </div>
                  <h4>{t('user.support.trustSafety.reportAbuse.title')}</h4>
                  <p>{t('user.support.trustSafety.reportAbuse.description')}</p>
                  <button className="support-btn primary">
                    <span className="material-icons">report</span>
                    {t('user.support.trustSafety.reportAbuse.button')}
                  </button>
                </div>

                <div className="safety-card">
                  <div className="safety-icon">
                    <span className="material-icons">security</span>
                  </div>
                  <h4>{t('user.support.trustSafety.accountSecurity.title')}</h4>
                  <p>{t('user.support.trustSafety.accountSecurity.description')}</p>
                  <button className="support-btn secondary">
                    <span className="material-icons">security</span>
                    {t('user.support.trustSafety.accountSecurity.button')}
                  </button>
                </div>

                <div className="safety-card">
                  <div className="safety-icon">
                    <span className="material-icons">privacy_tip</span>
                  </div>
                  <h4>{t('user.support.trustSafety.privacyCenter.title')}</h4>
                  <p>{t('user.support.trustSafety.privacyCenter.description')}</p>
                  <button className="support-btn secondary">
                    <span className="material-icons">privacy_tip</span>
                    {t('user.support.trustSafety.privacyCenter.button')}
                  </button>
                </div>

                <div className="safety-card">
                  <div className="safety-icon">
                    <span className="material-icons">verified_user</span>
                  </div>
                  <h4>{t('user.support.trustSafety.communityGuidelines.title')}</h4>
                  <p>{t('user.support.trustSafety.communityGuidelines.description')}</p>
                  <button className="support-btn secondary">
                    <span className="material-icons">verified_user</span>
                    {t('user.support.trustSafety.communityGuidelines.button')}
                  </button>
                </div>
              </div>

              <div className="safety-faq">
                <h4>{t('user.support.trustSafety.faq.title')}</h4>
                <div className="faq-list">
                  <div className="faq-item">
                    <h5>{t('user.support.trustSafety.faq.reportContent.question')}</h5>
                    <p>{t('user.support.trustSafety.faq.reportContent.answer')}</p>
                  </div>
                  
                  <div className="faq-item">
                    <h5>{t('user.support.trustSafety.faq.violations.question')}</h5>
                    <p>{t('user.support.trustSafety.faq.violations.answer')}</p>
                  </div>
                  
                  <div className="faq-item">
                    <h5>{t('user.support.trustSafety.faq.dataProtection.question')}</h5>
                    <p>{t('user.support.trustSafety.faq.dataProtection.answer')}</p>
                  </div>
                  
                  <div className="faq-item">
                    <h5>{t('user.support.trustSafety.faq.dataDeletion.question')}</h5>
                    <p>{t('user.support.trustSafety.faq.dataDeletion.answer')}</p>
                  </div>
                </div>
              </div>

              <div className="emergency-contact">
                <h4>{t('user.support.trustSafety.emergencyContact.title')}</h4>
                <p>{t('user.support.trustSafety.emergencyContact.description')}</p>
                <div className="emergency-options">
                  <a href="mailto:safety@nativelike.com" className="support-btn danger">
                    <span className="material-icons">warning</span>
                    {t('user.support.trustSafety.emergencyContact.safetyEmail')}
                  </a>
                  <a href="tel:+212612345678" className="support-btn danger">
                    <span className="material-icons">phone</span>
                    {t('user.support.trustSafety.emergencyContact.emergencyPhone')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="support-section">
      <div className="support-tabs">
        <div className="support-tabs-list">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`support-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="material-icons">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
          </div>
          
      <div className="support-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default UserSectionSupport; 